import {
  Dialog,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
} from '@citibox-panel/ui';
import type { TrackingMatch } from '@citibox-panel/api-client';
import { formatDeliveryDateTime } from '../../../lib/formatDeliveryDateTime';

export interface TrackingMatchesDialogProps {
  open: boolean;
  matches: TrackingMatch[];
  // The window's TOTAL, which is not `matches.length`: the read caps its rows, so the two numbers
  // differ exactly when the answer is truncated — and that is the case this dialog has to say out
  // loud rather than show a plausible-looking list for.
  count: number;
  onClose: () => void;
  // «The modal is really gone and the page is addressable again». The caller acts on the pick HERE
  // and not in `onPick`: while the dialog is up MUI marks the whole app root `aria-hidden`, so a
  // tab opened earlier is born where assistive tech is not looking.
  onExited: () => void;
  // Remembers the pick; it is the caller that acts on it, and only once the dialog has left.
  onPick: (match: TrackingMatch) => void;
}

// The columns of the agreed design, in its order. A constant rather than inline markup so the
// header and the body cannot drift into disagreeing about how many there are.
// TODO: i18n
const COLUMNS = ['FECHA DE ENTREGA', 'DIRECCIÓN', 'TELÉFONO', 'NOMBRE'] as const;

// Fixed sizing, or the address column takes whatever the longest street name asks for and pushes
// the name off the paper.
const COLUMN_WIDTHS = ['22%', '38%', '20%', '20%'];

// What any cell with nothing to say shows. The em dash is the panel's standing answer for an absent
// value, so a nameless titular — or an address the payload did not carry — reads as «not available»
// and not as a blank somebody forgot to fill.
const EM_DASH = '—';

export function TrackingMatchesDialog({
  open,
  matches,
  count,
  onClose,
  onExited,
  onPick,
}: TrackingMatchesDialogProps) {
  const truncated = count > matches.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onExited={onExited}
      // TODO: i18n
      label="Coincidencias del número de tracking"
      maxWidth="md"
      titleDivider
      title={
        <Text variant="overline" color="secondary" bold dense>
          {/* The window is named beside the count, the same rótulo the not-found notice carries.
              TODO: i18n */}
          {`${String(count)} ${count === 1 ? 'COINCIDENCIA' : 'COINCIDENCIAS'} — ÚLTIMOS 90 DÍAS`}
        </Text>
      }
    >
      {matches.length === 0 ? (
        // The dialog announces itself, so a second live region here would double or lose the fact.
        // TODO: i18n
        <EmptyState
          title="Sin coincidencias en los últimos 90 días"
          description="Comprueba el número de tracking."
          announce={false}
        />
      ) : (
        <>
          {truncated && (
            // TODO: i18n
            <Text variant="body2" color="secondary">
              {`Se muestran ${String(matches.length)} de ${String(count)} coincidencias. Afina el número de tracking.`}
            </Text>
          )}
          {/* TODO: i18n */}
          <Table layout="fixed" columnWidths={COLUMN_WIDTHS} aria-label="Coincidencias por tracking">
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column} component="th" scope="col">
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {matches.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => {
                    onPick(row);
                  }}
                >
                  <TableCell singleLine>
                    {formatDeliveryDateTime(row.storedDate, row.lockerTimezone)}
                  </TableCell>
                  <TableCell>{row.location.address ?? EM_DASH}</TableCell>
                  {/* Third-party PII, licensed by ADR 0020 because the case cannot be resolved
                      without it. Rendered as CHILDREN — inert text — never as markup, and never
                      reaching telemetry or the URL. */}
                  <TableCell singleLine>{row.recipient?.phone ?? EM_DASH}</TableCell>
                  <TableCell>{row.recipient?.name ?? EM_DASH}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Dialog>
  );
}
